import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateOrderTable,
  updateTableChange,
} from "../../reduxStore/slices/cartSlice";
import { useNotify } from "../../utils/snackbarUtils";
import AppButton from "../app-button/AppButton";
import AppDialog from "../app-dialog/AppDialog";
import Iconify from "../iconify/Iconify";
import TableSelectionAutocompleteInput from "./TableSelectionAutocompleteInput";

function TableChangeDialog({ isTableChangeDialogOpen, onDialogCancel }) {
  const dispatch = useDispatch();
  const { showSuccess, handleApiError, showError } = useNotify();
  const { user } = useSelector((state) => state.auth);

  const { tablesWithStatus, activeOrderId, orderId, selectedTable } =
    useSelector((state) => state.cart);

  const storeId = user?.storeId;

  const [isLoading, setIsLoading] = useState(false);
  const [splitChips, setSplitChips] = useState(["T3-1", "T3-2"]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [newTable, setNewTable] = useState("");
  const [tables, setTables] = useState([]);
  const [merge, setMerge] = useState(true);
  const [split, setSplit] = useState(true);

  const dialogTitle = selectedTable?.table?.table_name
    ? "Move Order to Another Table"
    : "Assign Order to Table";
    
  const baseTable = "T3 (Garden)";

  const handleSaveClick = async () => {
    if (!newTable?.dine_area_table_id) {
      showError("Please select a new table.");
      return;
    }
    try {
      setIsLoading(true);
      const obj = {
        store_id: storeId,
        order_id: orderId,
        order_uid: activeOrderId,
        dine_area_table_uid: newTable.dine_area_table_id,
        order_status_id: selectedTable?.table?.order_dine_table_status_id || "",
        order_dine_table_id: selectedTable?.table?.order_dine_table_id || "",
        order_dine_table_uid: selectedTable?.table?.order_dine_table_uid || "",
      };
      if (selectedTable?.table?.order_dine_table_uid) {
        await dispatch(updateOrderTable(obj)).unwrap();
      }
      // update redux state tables
      await dispatch(updateTableChange(newTable)).unwrap();
      onDialogCancel();
      setIsLoading(false);
      showSuccess("Table changed successfully.");
    } catch (err) {
      setIsLoading(false);
      handleApiError(err);
    }
  };

  const handleTableChange = (_, value) => {
    setNewTable(value);
  };

  const handleSelectTable = (id) => {
    setSelectedTables((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleAddChip = () => {
    const nextNumber = splitChips.length + 1;
    setSplitChips([...splitChips, `T3-${nextNumber}`]);
  };

  const handleRemoveChip = (index) => {
    const newChips = splitChips.filter((_, i) => i !== index);
    // reindex chips
    const reIndexed = newChips.map((_, i) => `T3-${i + 1}`);
    setSplitChips(reIndexed);
  };

  const dialogTableChangeFooter = (
    <>
      <Box
        style={{
          display: "flex",
          gap: 15,
        }}
      >
        <Button size="medium" onClick={onDialogCancel}>
          Cancel
        </Button>
        <AppButton
          disabled={isLoading}
          isLoading={isLoading}
          fullWidth
          size="medium"
          variant="contained"
          onClick={handleSaveClick}
        >
          Move Order
        </AppButton>
      </Box>
    </>
  );

  return (
    <>
      <AppDialog
        maxWidth="xs"
        isOpen={isTableChangeDialogOpen}
        id={"move_order_another_dialog"}
        title={<>{dialogTitle}</>}
        footer={dialogTableChangeFooter}
      >
        <Box
          style={{
            margin: "20px 5px",
          }}
        >
          <Typography variant="body1" sx={{ mb: 4 }}>
            Current Table:{" "}
            {selectedTable?.table?.table_name && (
              <strong>
                {selectedTable?.table?.table_name} (
                {selectedTable?.dine_area_name})
              </strong>
            )}
          </Typography>
          {/* <Typography variant="body1" sx={{ mb: 3 }}>
            Assigned to: <b>Rajendra A</b>
          </Typography> */}

          <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <TableSelectionAutocompleteInput
                selectedTable={selectedTable}
                tablesWithStatus={tablesWithStatus}
                value={newTable}
                onChange={handleTableChange}
              />
            </FormControl>

            {selectedTable?.table?.order_dine_table_uid && (
              <Box
                sx={{
                  my: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "#f8f9fa",
                  borderLeft: "4px solid #ffc107",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Iconify
                  icon="mdi:alert-circle-outline"
                  width={20}
                  height={20}
                  color="#ffc107"
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    color: "#6c757d",
                    fontStyle: "italic",
                  }}
                >
                  Moving this order will reset the old table{" "}
                  <strong>
                    {selectedTable?.table?.table_name} (
                    {selectedTable?.dine_area_name})
                  </strong>{" "}
                  to Free status.
                </Typography>
              </Box>
            )}
            {/* Options */}
            {false && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                    // checked={merge}
                    // onChange={(e) => setMerge(e.target.checked)}
                    />
                  }
                  label="Merge with another table"
                />

                {/* Additional tables if merging */}
                {merge && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Select additional tables:
                    </Typography>
                    <FormGroup>
                      {tables.map((table) => (
                        <FormControlLabel
                          key={table.id}
                          control={
                            <Checkbox
                              checked={selectedTables.includes(table.id)}
                              onChange={() => handleSelectTable(table.id)}
                              disabled={table.disabled}
                            />
                          }
                          label={`${table.label} - ${table.status}`}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                {merge && selectedTables.length > 0 && (
                  <Box
                    sx={{ mt: 2, p: 2, bgcolor: "#f0f4f8", borderRadius: 2 }}
                  >
                    <Typography variant="body2">
                      Result: <b>T3 (Garden)</b> +{" "}
                      {selectedTables
                        .map((id) => tables.find((t) => t.id === id)?.label)
                        .join(", ")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Orders will be combined into one bill.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {false && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                    // checked={split}
                    // onChange={(e) => setSplit(e.target.checked)}
                    />
                  }
                  label="Split table"
                />
              </Box>
            )}
            {/* Split Chips */}
            {split && false && (
              <Box sx={{ mt: 3, p: 2, bgcolor: "#fff3e0", borderRadius: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Split {baseTable} into:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {splitChips.map((chip, index) => (
                    <Chip
                      key={chip}
                      label={chip}
                      onDelete={() => handleRemoveChip(index)}
                      deleteIcon={<Iconify icon={"material-symbols:close"} />}
                      sx={{ mb: 1 }}
                    />
                  ))}
                  <IconButton
                    size="small"
                    onClick={handleAddChip}
                    color="primary"
                  >
                    <Iconify icon={"material-symbols:add"} />
                  </IconButton>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Orders will be billed separately per sub-table.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </AppDialog>
    </>
  );
}

export default TableChangeDialog;
